// apps/spa/src/components/reminders/ChannelPicker.tsx
// REMIND-06 — shadcn Combobox (Popover + Command/cmdk) grouped by category.
//
// Controlled component:
//   value:    channel ID (snowflake) or '' for unselected
//   onChange: (channelId: string) => void
//
// Reads useChannels(); groups by parentName via groupChannels(); renders
// CommandGroup heading={category} for each group; sorts channels within
// group by Discord position; sorts groups by min(child.position).
// CommandInput at top filters by case-insensitive substring on ch.name
// (cmdk's built-in filter uses the `value` prop on each CommandItem).
//
// PopoverTrigger button shows current selection ('#general') or 'Select channel...'.

import { useMemo, useState } from 'react';
import { ChevronsUpDown } from 'lucide-react';
import type { ChannelOption } from '@guardian/shared';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { useChannels } from '@/hooks/useChannels';

export interface ChannelPickerProps {
  value: string;
  onChange: (channelId: string) => void;
  disabled?: boolean;
}

const UNGROUPED = '__ungrouped__';

interface GroupedChannels {
  /** Display name for the group (parentName or 'Channels' for ungrouped). */
  category: string;
  /** Min position across the group's children — used for inter-group sort. */
  minPos: number;
  /** Children sorted by their own position. */
  children: ChannelOption[];
}

/**
 * Group channels by parentName (Discord category). Channels with no parent
 * land under 'Channels'. Groups sort by min(child.position); children sort
 * by position within their group. RESEARCH Pattern 2.
 */
export function groupChannels(channels: ChannelOption[]): GroupedChannels[] {
  const buckets = new Map<string, ChannelOption[]>();
  for (const ch of channels) {
    const key = ch.parentName ?? UNGROUPED;
    const arr = buckets.get(key) ?? [];
    arr.push(ch);
    buckets.set(key, arr);
  }
  const groups: GroupedChannels[] = [];
  for (const [key, children] of buckets.entries()) {
    children.sort((a, b) => a.position - b.position);
    const minPos = children.length > 0 ? children[0].position : 0;
    const category = key === UNGROUPED ? 'Channels' : key;
    groups.push({ category, minPos, children });
  }
  groups.sort((a, b) => a.minPos - b.minPos);
  return groups;
}

export function ChannelPicker({
  value,
  onChange,
  disabled,
}: ChannelPickerProps) {
  const [open, setOpen] = useState(false);
  const channelsQuery = useChannels();

  const channels = channelsQuery.data ?? [];
  const grouped = useMemo(() => groupChannels(channels), [channels]);
  const current = useMemo(
    () => channels.find((c) => c.id === value),
    [channels, value],
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        aria-label="Select channel"
        aria-expanded={open}
        disabled={disabled || channelsQuery.isLoading}
        className={cn(
          // Mirror Button outline variant — base-nova PopoverTrigger renders a
          // native <button>, so we apply the same Tailwind shape directly.
          'inline-flex w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm font-normal shadow-xs transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
          !value && 'text-muted-foreground',
        )}
      >
        {current ? `#${current.name}` : 'Select channel...'}
        <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
        <Command
          // cmdk's default filter is substring-match on each item's `value`
          // prop. We set value={ch.name} on items below so typing 'raid'
          // narrows to channels whose name includes 'raid'.
          filter={(itemValue, search) => {
            return itemValue.toLowerCase().includes(search.toLowerCase())
              ? 1
              : 0;
          }}
        >
          <CommandInput placeholder="Search channels..." />
          <CommandList>
            <CommandEmpty>No channels found.</CommandEmpty>
            {grouped.map((group) => (
              <CommandGroup key={group.category} heading={group.category}>
                {group.children.map((ch) => (
                  <CommandItem
                    key={ch.id}
                    value={ch.name}
                    onSelect={() => {
                      onChange(ch.id);
                      setOpen(false);
                    }}
                  >
                    #{ch.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
